'use client';

import Icon from '@/components/ui/AppIcon';
import DemoDataPill from './DemoDataPill';

const cardStyle = {
  background: '#FFFFFF',
  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
  border: '1px solid #F0F0F0',
};

export default function Row7OutsideTheBuilding() {
  return (
    <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <HiringPipeline />
      <BoardMeetingHub />
      <StrategicInitiatives />
      <RunRateVsPlan />
    </section>
  );
}

// ── 1. Hiring Pipeline ─────────────────────────────────────────────────────

interface OpenRole {
  title: string;
  department: string;
  sourcing: number;
  interview: number;
  offer: number;
  daysOpen: number;
}

const OPEN_ROLES: OpenRole[] = [];

function HiringPipeline() {
  // Guard: empty roles list would divide by zero → NaN.
  const avgDaysToHire = OPEN_ROLES.length
    ? Math.round(OPEN_ROLES.reduce((s, r) => s + r.daysOpen, 0) / OPEN_ROLES.length)
    : 0;

  return (
    <div className="relative rounded-xl p-5" style={cardStyle}>
      <DemoDataPill />
      <div className="flex items-start justify-between gap-2 mb-3 pr-16">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Icon name="BriefcaseIcon" size={15} style={{ color: '#07B0E9' }} />
            <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
              Hiring Pipeline
            </h3>
          </div>
          <p className="text-xs text-gray-400 mt-0.5">
            {OPEN_ROLES.length} open roles · Avg {avgDaysToHire}d to hire
          </p>
        </div>
      </div>
      <div className="space-y-2">
        {OPEN_ROLES.map((r) => {
          const totalCandidates = r.sourcing + r.interview + r.offer;
          return (
            <div
              key={r.title}
              className="rounded-lg px-2.5 py-2"
              style={{ border: '1px solid #F3F4F6' }}
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <p className="text-[11px] font-semibold text-gray-800 truncate">{r.title}</p>
                <span className="text-[10px] text-gray-500 flex-shrink-0">{r.daysOpen}d open</span>
              </div>
              <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-1">{r.department}</p>
              {totalCandidates === 0 ? (
                <span
                  className="rounded-full px-2 py-0.5 text-[9px] font-bold"
                  style={{ background: 'rgba(7,176,233,0.08)', color: '#0369A1' }}
                >
                  Just opened
                </span>
              ) : (
                <div className="flex items-center gap-1 flex-wrap">
                  {r.sourcing > 0 && (
                    <span className="rounded-full px-2 py-0.5 text-[9px] font-bold" style={{ background: 'rgba(7,176,233,0.10)', color: '#0369A1' }}>
                      Sourcing {r.sourcing}
                    </span>
                  )}
                  {r.interview > 0 && (
                    <span className="rounded-full px-2 py-0.5 text-[9px] font-bold" style={{ background: 'rgba(245,158,11,0.10)', color: '#92400E' }}>
                      Interview {r.interview}
                    </span>
                  )}
                  {r.offer > 0 && (
                    <span className="rounded-full px-2 py-0.5 text-[9px] font-bold" style={{ background: 'rgba(16,185,129,0.10)', color: '#047857' }}>
                      Offer {r.offer}
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <a
        href="#"
        className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold rounded-lg px-2.5 py-1.5 hover:opacity-90 active:scale-95 transition-all"
        style={{ background: 'rgba(7,176,233,0.10)', color: '#0369A1' }}
      >
        View ATS
        <Icon name="ArrowRightIcon" size={10} />
      </a>
    </div>
  );
}

// ── 2. Board Meeting Hub ───────────────────────────────────────────────────

const NEXT_BOARD_MEETING = {
  date: '—',
  daysAway: 0,
};

type AgendaStatus = 'Ready' | 'Draft' | 'Late';
type ActionStatus = 'Done' | 'In Progress' | 'Overdue';

interface AgendaItem  { topic: string; owner: string; status: AgendaStatus; }
interface ActionItem  { topic: string; owner: string; status: ActionStatus; }

const AGENDA_ITEMS: AgendaItem[] = [];

const LAST_ACTION_ITEMS: ActionItem[] = [];

function agendaPill(s: AgendaStatus): { color: string; bg: string } {
  if (s === 'Ready') return { color: '#047857', bg: 'rgba(16,185,129,0.10)' };
  if (s === 'Draft') return { color: '#0369A1', bg: 'rgba(7,176,233,0.10)' };
  return                     { color: '#B91C1C', bg: 'rgba(239,68,68,0.10)' };
}

function actionPill(s: ActionStatus): { color: string; bg: string } {
  if (s === 'Done')        return { color: '#047857', bg: 'rgba(16,185,129,0.10)' };
  if (s === 'In Progress') return { color: '#92400E', bg: 'rgba(245,158,11,0.10)' };
  return                          { color: '#B91C1C', bg: 'rgba(239,68,68,0.10)' };
}

function BoardMeetingHub() {
  return (
    <div className="relative rounded-xl p-5" style={cardStyle}>
      <DemoDataPill />
      <div className="flex items-start justify-between gap-2 mb-3 pr-16">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Icon name="CalendarDaysIcon" size={15} style={{ color: '#033494' }} />
            <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
              Next Board Meeting · {NEXT_BOARD_MEETING.date}
            </h3>
          </div>
          <p className="text-xs text-gray-400 mt-0.5">
            {NEXT_BOARD_MEETING.daysAway} days away · {AGENDA_ITEMS.length} agenda items due
          </p>
        </div>
      </div>

      <div className="mb-3">
        <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-1.5">
          Agenda due by {NEXT_BOARD_MEETING.date}
        </p>
        <div className="space-y-1">
          {AGENDA_ITEMS.map((a, i) => {
            const p = agendaPill(a.status);
            return (
              <div key={i} className="flex items-center justify-between gap-2 text-[11px]">
                <span className="text-gray-700 truncate">
                  {a.topic} <span className="text-gray-400">· {a.owner}</span>
                </span>
                <span
                  className="rounded-full px-2 py-0.5 text-[9px] font-bold flex-shrink-0"
                  style={{ background: p.bg, color: p.color }}
                >
                  {a.status}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="pt-3 border-t border-gray-100">
        <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-1.5">
          Last meeting actions
        </p>
        <div className="space-y-1">
          {LAST_ACTION_ITEMS.map((a, i) => {
            const p = actionPill(a.status);
            return (
              <div key={i} className="flex items-center justify-between gap-2 text-[11px]">
                <span className="text-gray-700 truncate">
                  {a.topic} <span className="text-gray-400">· {a.owner}</span>
                </span>
                <span
                  className="rounded-full px-2 py-0.5 text-[9px] font-bold flex-shrink-0"
                  style={{ background: p.bg, color: p.color }}
                >
                  {a.status}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <a
        href="#"
        className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold rounded-lg px-2.5 py-1.5 hover:opacity-90 active:scale-95 transition-all"
        style={{ background: 'rgba(3,52,148,0.10)', color: '#033494' }}
      >
        Open board portal
        <Icon name="ArrowRightIcon" size={10} />
      </a>
    </div>
  );
}

// ── 3. Strategic Initiatives ───────────────────────────────────────────────

type InitiativeStatus = 'On Track' | 'At Risk' | 'Blocked';

interface Initiative {
  name: string;
  owner: string;
  progress: number; // 0–100
  eta: string;
  status: InitiativeStatus;
}

const INITIATIVES: Initiative[] = [];

// Sort by status severity so attention-needed items bubble to top.
// % complete sorting would surface easy wins; this view is for Co-CEOs who
// need to see what's stuck before what's almost done.
const SEVERITY_WEIGHT: Record<InitiativeStatus, number> = { Blocked: 3, 'At Risk': 2, 'On Track': 1 };

function initiativeStatusPill(s: InitiativeStatus): { color: string; bg: string; bar: string } {
  if (s === 'Blocked')  return { color: '#B91C1C', bg: 'rgba(239,68,68,0.15)',  bar: '#EF4444' };
  if (s === 'At Risk')  return { color: '#92400E', bg: 'rgba(245,158,11,0.15)', bar: '#F59E0B' };
  return                       { color: '#047857', bg: 'rgba(16,185,129,0.15)', bar: '#10B981' };
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  return (parts[0]?.[0] ?? '?').toUpperCase();
}

function StrategicInitiatives() {
  const sorted = [...INITIATIVES].sort(
    (a, b) => SEVERITY_WEIGHT[b.status] - SEVERITY_WEIGHT[a.status],
  );

  return (
    <div className="relative rounded-xl p-5" style={cardStyle}>
      <DemoDataPill />
      <div className="flex items-start justify-between gap-2 mb-3 pr-16">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Icon name="FlagIcon" size={15} style={{ color: '#8B5CF6' }} />
            <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
              Strategic Initiatives
            </h3>
          </div>
          <p className="text-xs text-gray-400 mt-0.5">{INITIATIVES.length} active</p>
        </div>
      </div>
      <div className="space-y-2">
        {sorted.map((it) => {
          const p = initiativeStatusPill(it.status);
          return (
            <div key={it.name} className="rounded-lg px-2.5 py-2" style={{ border: '1px solid #F3F4F6' }}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="flex items-center gap-1.5 min-w-0">
                  <div
                    className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0"
                    style={{ background: 'linear-gradient(135deg, #07B0E9 0%, #033494 100%)' }}
                  >
                    {initialsOf(it.owner)}
                  </div>
                  <p className="text-[11px] font-semibold text-gray-800 truncate">{it.name}</p>
                </div>
                <span
                  className="rounded-full px-2 py-0.5 text-[9px] font-bold flex-shrink-0"
                  style={{ background: p.bg, color: p.color }}
                >
                  {it.status}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 rounded-full" style={{ background: '#F3F4F6' }}>
                  <div
                    className="h-1.5 rounded-full"
                    style={{ width: `${it.progress}%`, background: p.bar }}
                  />
                </div>
                <span className="text-[10px] font-bold text-gray-600 w-8 text-right">{it.progress}%</span>
              </div>
              <p className="text-[10px] text-gray-400 mt-1">ETA {it.eta}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── 4. Run Rate vs Plan ────────────────────────────────────────────────────

const RUN_RATE = {
  annualizedMillions: 0,
  planMillions:       0,
  ytdActualMillions:  0,
};

function RunRateVsPlan() {
  const { annualizedMillions, planMillions, ytdActualMillions } = RUN_RATE;
  // Guard: no plan → avoid divide-by-zero NaN in the delta.
  const deltaPct = planMillions > 0 ? ((annualizedMillions - planMillions) / planMillions) * 100 : 0;
  const ahead = deltaPct >= 0;

  // Bar segments: total = max(annualized, plan) so the bar always renders the
  // full picture without overflowing. When ahead, "above plan" is the cyan tip.
  // Guard: total 0 (empty data) → all segment widths collapse to 0 instead of NaN.
  const total = Math.max(annualizedMillions, planMillions);
  const ytdPct       = total > 0 ? (ytdActualMillions / total) * 100 : 0;
  const planRemPct   = total > 0 ? Math.max(0, ((planMillions - ytdActualMillions) / total)) * 100 : 0;
  const abovePlanPct = total > 0 ? Math.max(0, ((annualizedMillions - planMillions) / total)) * 100 : 0;

  return (
    <div className="relative rounded-xl p-5" style={cardStyle}>
      <DemoDataPill />
      <div className="flex items-start justify-between gap-2 mb-1 pr-16">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Icon name="PresentationChartLineIcon" size={15} style={{ color: '#10B981' }} />
            <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
              Run Rate vs Plan
            </h3>
          </div>
          <p className="text-xs text-gray-400 mt-0.5">Annualized from YTD</p>
        </div>
      </div>

      <div className="flex items-baseline gap-2 mt-2 mb-2">
        <p
          className="text-4xl font-bold leading-none"
          style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}
        >
          ${annualizedMillions.toFixed(1)}M
        </p>
        <span
          className="text-xs font-bold"
          style={{ color: ahead ? '#10B981' : '#EF4444' }}
        >
          {ahead ? '▲' : '▼'} {Math.abs(deltaPct).toFixed(0)}% vs plan
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 mt-3 mb-3">
        <div className="rounded-lg p-2" style={{ border: '1px solid #F3F4F6' }}>
          <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-500">Plan</p>
          <p className="text-sm font-bold" style={{ color: '#000150' }}>
            ${planMillions.toFixed(1)}M
          </p>
        </div>
        <div className="rounded-lg p-2" style={{ border: '1px solid #F3F4F6' }}>
          <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-500">YTD actual</p>
          <p className="text-sm font-bold" style={{ color: '#000150' }}>
            ${ytdActualMillions.toFixed(1)}M
          </p>
        </div>
      </div>

      <div className="flex h-3 rounded-full overflow-hidden mb-2" style={{ background: '#F3F4F6' }}>
        <div style={{ width: `${ytdPct}%`,       background: '#10B981' }} title="YTD actual" />
        <div style={{ width: `${planRemPct}%`,   background: 'rgba(16,185,129,0.25)' }} title="Pace to plan" />
        {abovePlanPct > 0 && (
          <div style={{ width: `${abovePlanPct}%`, background: '#07B0E9' }} title="Above plan" />
        )}
      </div>
      <div className="flex items-center gap-2 text-[10px] text-gray-500">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: '#10B981' }} /> YTD</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: 'rgba(16,185,129,0.25)' }} /> Pace to plan</span>
        {abovePlanPct > 0 && (
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: '#07B0E9' }} /> Above</span>
        )}
      </div>
    </div>
  );
}
